import React from 'react';
import { CheckCircle, Target, Lightbulb } from 'lucide-react';
import { aboutData, contactInfo } from '../data/mock';
import { FounderPortrait } from '../components/FounderPortrait';

const About = () => {
  return (
    <main className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0D234E] to-[#1a3a6e] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="about-title">
              {aboutData.title}
            </h1>
            <p className="text-xl sm:text-2xl text-[#BFB47E] font-serif mb-6" data-testid="about-subtitle">
              {aboutData.subtitle}
            </p>
            <p className="text-base sm:text-xl text-gray-300 leading-relaxed" data-testid="about-description">
              {aboutData.description}
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="w-14 h-14 bg-[#0D234E] rounded-lg flex items-center justify-center text-[#BFB47E] mb-6">
                <Target size={28} />
              </div>
              <h2 className="text-base md:text-lg font-bold text-[#0D234E] mb-4" data-testid="about-mission-title">Our Mission</h2>
              <p className="text-gray-700 leading-relaxed text-base" data-testid="about-mission">
                {aboutData.mission}
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="w-14 h-14 bg-[#0D234E] rounded-lg flex items-center justify-center text-[#BFB47E] mb-6">
                <Lightbulb size={28} />
              </div>
              <h2 className="text-base md:text-lg font-bold text-[#0D234E] mb-4" data-testid="about-vision-title">Our Vision</h2>
              <p className="text-gray-700 leading-relaxed text-base" data-testid="about-vision">
                {aboutData.vision}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section with Founder Image */}
      <section className="py-20 bg-gray-50" data-testid="about-founder-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="min-w-0">
              <h2 className="text-base md:text-lg font-bold text-[#0D234E] mb-6" data-testid="about-story-title">
                Built on Results, Driven by Passion
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6 text-base" data-testid="about-story-origin">
                DMARKETINGCHAMP was founded on a simple belief: every business deserves access to world-class digital marketing strategies that deliver real, measurable results.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6 text-base" data-testid="about-story-growth">
                Over the years, we've helped hundreds of businesses—from solo entrepreneurs to established e-commerce brands—transform their online presence and achieve sustainable growth.
              </p>
              <p className="text-gray-700 leading-relaxed text-base" data-testid="about-story-team">
                Our team combines deep industry expertise with cutting-edge strategies, staying ahead of digital trends to ensure our clients always have a competitive advantage.
              </p>
            </div>
            
            <FounderPortrait />
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-base md:text-lg font-bold text-[#0D234E] mb-4" data-testid="about-values-title">
              Our Core Values
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto" data-testid="about-values-subtitle">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {aboutData.values.map((value, index) => (
              <div
                key={index}
                className="flex items-start space-x-4 p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#BFB47E] bg-opacity-20 rounded-full flex items-center justify-center">
                    <CheckCircle className="text-[#0D234E]" size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#0D234E] mb-2" data-testid={`about-value-title-${index}`}>
                    {value.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed" data-testid={`about-value-description-${index}`}>
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Stats */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base md:text-lg font-bold text-[#0D234E] mb-4" data-testid="about-impact-title">
              Our Impact
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {[
              { key: 'size', label: 'Team Members' },
              { key: 'experience', label: 'Years Experience' },
              { key: 'clients', label: 'Happy Clients' },
              { key: 'industries', label: 'Industries Served' }
            ].map(({ key, label }) => (
              <div key={key} className="min-w-0 text-center bg-white p-4 sm:p-6 rounded-lg shadow-md">
                <div className="text-3xl sm:text-4xl font-bold text-[#0D234E] mb-2 break-words" data-testid={`about-stat-${key}`}>{aboutData.team[key]}</div>
                <div className="text-sm sm:text-base text-gray-600 font-medium" data-testid={`about-stat-label-${key}`}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#0D234E] to-[#1a3a6e] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-base md:text-lg font-bold mb-6" data-testid="about-contact-title">
            Ready to Partner with Us?
          </h2>
          <p className="text-base mb-8 text-gray-300" data-testid="about-contact-description">
            Let's build your network and secure your future together
          </p>
          <a
            href={contactInfo.whatsapp}
            data-testid="about-contact-whatsapp"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#BFB47E] text-[#0D234E] px-8 py-4 rounded-md font-semibold hover:bg-[#d4c991] transition-colors duration-200 hover:shadow-xl"
          >
            Get in Touch
          </a>
        </div>
      </section>
    </main>
  );
};

export default About;
